<script setup lang="ts">
import { computed, type Component } from 'vue';
import { CircleHelp, Repeat } from 'lucide-vue-next';
import LightningIcon from '@/assets/icons/schedule/lightning.svg?component';
import SpaceBarIcon from '@/assets/icons/schedule/space_bar.svg?component';
import MessageIcon from '@/assets/icons/schedule/message.svg?component';
import SilenceIcon from '@/assets/icons/schedule/silence.svg?component';
import DoubleArrowIcon from '@/assets/icons/schedule/double_arrow.svg?component';
import { SCHEDULE_KIND_LABELS, type ScheduleKind } from '@/utils/customPlaylistSchedule';

/**
 * The type glyph for one schedule (decision D10). React's own icons, recoloured
 * to currentColor so they follow the text colour in light and dark; recurring
 * is lucide's Repeat, and station change (which React never had an icon for)
 * is React's double arrow.
 */
const props = defineProps<{
    kind: ScheduleKind;
    /**
     * Set where the type's name is written next to the icon, as in the editor's
     * type menu. The icon is then hidden from screen readers, so the name is
     * read once, not twice ("Recurring Recurring").
     */
    decorative?: boolean;
}>();

const ICONS: Record<ScheduleKind, Component> = {
    oneoff: LightningIcon,
    recurring: Repeat,
    insert: SpaceBarIcon,
    message: MessageIcon,
    silence: SilenceIcon,
    stationchange: DoubleArrowIcon,
    unknown: CircleHelp,
};

const icon = computed(() => ICONS[props.kind]);
const label = computed(() => SCHEDULE_KIND_LABELS[props.kind]);
</script>

<template>
    <component
        :is="icon"
        class="size-3.5 shrink-0"
        :role="decorative ? undefined : 'img'"
        :aria-label="decorative ? undefined : label"
        :aria-hidden="decorative ? 'true' : undefined"
        :data-kind="kind"
        data-testid="schedule-type-icon"
    />
</template>
